import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ToastContainer, toast } from 'react-toastify'
import Select from 'react-select'
import { useForm } from 'react-hook-form'
import { Table, Button } from 'react-bootstrap'
import axios from 'axios'
import Papa from 'papaparse'
import Modal from './ui/Modal'
import { EditIcon } from './Icons'
import ConfirmModal from './ui/ConfirmModal'

function ExcelImport() {
  const { id } = useParams()

  const [csvData, setCsvData] = useState([])

  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true) // Loading state
  const navigate = useNavigate()

  useEffect(() => {
    const fetchData = async () => {
      try {
        await getTests()
      } catch (error) {
        console.error('Error loading data:', error)
        setIsLoading(false) // Stop loading even if there's an error
      }
    }
    fetchData()
  }, [])

  const [topics, setTopics] = useState([])
  const [subtopics, setSubtopics] = useState([])
  const [filteredSubtopics, setFilteredSubtopics] = useState([])
  const [selectedTopic, setSelectedTopic] = useState('')
  const [selectedSubtopic, setSelectedSubtopic] = useState('')
  const [selectedQuestion, setSelectedQuestion] = useState(null)

  useEffect(() => {
    if (!selectedQuestion || !selectedQuestion.topic) return // Ensure selectedQuestion is defined

    const getTopicsAndSubtopics = async () => {
      try {
        const [topicsResponse, subtopicsResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BASE_URL}getalltopics`),
          axios.get(
            `${process.env.REACT_APP_BASE_URL}getallsubtopics?topic_id=${selectedQuestion.topic}`
          ),
        ])

        if (topicsResponse.status === 200) setTopics(topicsResponse.data)
        if (subtopicsResponse.status === 200)
          setSubtopics(subtopicsResponse.data)
      } catch (error) {
        console.error('Error fetching topics/subtopics:', error)
      }
    }

    getTopicsAndSubtopics()
  }, [selectedQuestion?.topic]) // Only runs when selectedQuestion.topic changes

  useEffect(() => {
    if (selectedTopic) {
      const filtered = subtopics.filter(
        (subtopic) => subtopic.topic_id === selectedTopic
      )
      setFilteredSubtopics(filtered)
    } else {
      setFilteredSubtopics([])
    }
  }, [selectedTopic, subtopics]) // Separate filtering logic

  useEffect(() => {
    if (selectedTopic) {
      const filtered = subtopics.filter(
        (subtopic) => subtopic.topic_id === selectedTopic
      )
      setFilteredSubtopics(filtered)
    } else {
      setFilteredSubtopics([])
    }
  }, [selectedTopic, subtopics]) // Separate filtering logic

  const getTests = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_BASE_URL}getsatdata/${id}`
    )
    if (response.status === 200) {
      setData(response.data)
      setIsLoading(false) // Ensure loading is set to false after data is fetched
      navigate(`/excelimport/${id}`)
    }
  }

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [formData, setFormData] = useState([])

  const openModal = (question) => {
    setSelectedQuestion(question)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedQuestion(null)
  }

  const handleUpdate = async (e) => {
    e.preventDefault()

    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}updatecsv`,
        selectedQuestion
      )
      closeModal()
      window.location.href = `/excelimport/${id}` // Force full page reload
    } catch (error) {
      console.error('Error updating question:', error)
    }
  }

  const handleFileUpload = (event) => {
    const file = event.target.files[0]

    Papa.parse(file, {
      header: false, // No headers in this CSV format
      complete: handleParsedData,
    })
  }
  const handleParsedData = (parsedData) => {
    setCsvData(parsedData.data)
  }

  const [importModalOpen, setImportModalOpen] = useState(false)
  const [importModalMessage, setImportModalMessage] = useState('')

  const saveToMongoDB = () => {
    fetch(`${process.env.REACT_APP_BASE_URL}savecsv`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        testId: id,
        csvData: csvData,
      }),
    })
      .then((response) => {
        if (response.ok) {
          return response.text()
        } else {
          throw new Error('Failed to save data')
        }
      })
      .then((message) => {
        setImportModalMessage('Data saved successfully')
        setImportModalOpen(true)
      })
      .catch((error) => {
        setImportModalMessage('An error occurred while saving data')
        setImportModalOpen(true)
      })
  }

  const [activeTab, setActiveTab] = useState('readingWriting')

  return (
    <div className="w-full mt-2.5 max-md:max-w-full">
      {/* Header */}
      <div className="flex justify-between items-center border border-[#F0F0F0] bg-white px-5 py-4 rounded-t-[10px]">
        <h2 className="text-lg font-bold text-[#030303]">Question Upload</h2>
        <Link
          className="bg-[#DE3C7D] text-white px-4 py-2 rounded-md hover:bg-[#c72c6a] transition-colors font-medium"
          to="/sattests"
        >
          Back to SAT Exams
        </Link>
      </div>
      {/* CSV Import Section */}
      <div className="flex items-center gap-4 bg-white border-x border-[#F0F0F0] px-5 py-4">
        <input
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          className="block w-60 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#EFEFEF] file:text-[#DE3C7D] hover:file:bg-[#DE3C7D] hover:file:text-white"
        />
        <button
          className="bg-[#EFEFEF] text-[#DE3C7D] px-4 py-2 rounded-md border border-[#DE3C7D] font-medium hover:bg-[#DE3C7D] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={saveToMongoDB}
          disabled={!csvData || !Array.isArray(csvData) || csvData.length === 0}
        >
          Import Questions
        </button>
      </div>
      {/* Table Section */}
      <div className="bg-white border-x border-b border-[#F0F0F0] rounded-b-[10px] overflow-x-auto mt-2">
        <div className="flex items-center px-5 py-4 border-b border-[#F0F0F0]">
          <h3 className="text-lg font-semibold text-[#030303]">
            SAT Tests List
          </h3>
        </div>
        {isLoading ? (
          <div className="min-w-[1200px]">
            <div className="flex bg-neutral-50 border-b border-[#F0F0F0] font-medium text-[#535862] text-sm">
              <div className="w-16 px-4 py-3.5 border-r border-[#F0F0F0]">
                Q.No
              </div>
              <div className="w-96 px-4 py-3.5 border-r border-[#F0F0F0]">
                QuestionText
              </div>
              <div className="w-40 px-4 py-3.5 border-r border-[#F0F0F0]">
                Equation
              </div>
              <div className="w-32 px-4 py-3.5 border-r border-[#F0F0F0]">
                Choice A
              </div>
              <div className="w-32 px-4 py-3.5 border-r border-[#F0F0F0]">
                Choice B
              </div>
              <div className="w-32 px-4 py-3.5 border-r border-[#F0F0F0]">
                Choice C
              </div>
              <div className="w-32 px-4 py-3.5 border-r border-[#F0F0F0]">
                Choice D
              </div>
              <div className="w-32 px-4 py-3.5 border-r border-[#F0F0F0]">
                Correct Answer
              </div>
              <div className="w-40 px-4 py-3.5 border-r border-[#F0F0F0]">
                Topic
              </div>
              <div className="w-40 px-4 py-3.5 border-r border-[#F0F0F0]">
                Sub Topic
              </div>
              <div className="w-32 px-4 py-3.5 border-r border-[#F0F0F0]">
                Difficulty
              </div>
              <div className="w-32 px-4 py-3.5">Action</div>
            </div>
            {Array.from({ length: 5 }).map((_, idx) => (
              <div
                key={idx}
                className="flex border-b border-[#F0F0F0] bg-white animate-pulse"
              >
                <div className="w-16 px-4 py-2 border-r border-[#F0F0F0]">
                  <div className="h-4 w-full bg-gray-200 rounded" />
                </div>
                <div className="w-96 px-4 py-2 border-r border-[#F0F0F0]">
                  <div className="h-4 w-full bg-gray-200 rounded" />
                </div>
                <div className="w-40 px-4 py-2 border-r border-[#F0F0F0]">
                  <div className="h-4 w-full bg-gray-200 rounded" />
                </div>
                <div className="w-32 px-4 py-2 border-r border-[#F0F0F0]">
                  <div className="h-4 w-full bg-gray-200 rounded" />
                </div>
                <div className="w-32 px-4 py-2 border-r border-[#F0F0F0]">
                  <div className="h-4 w-full bg-gray-200 rounded" />
                </div>
                <div className="w-32 px-4 py-2 border-r border-[#F0F0F0]">
                  <div className="h-4 w-full bg-gray-200 rounded" />
                </div>
                <div className="w-32 px-4 py-2 border-r border-[#F0F0F0]">
                  <div className="h-4 w-full bg-gray-200 rounded" />
                </div>
                <div className="w-32 px-4 py-2 border-r border-[#F0F0F0]">
                  <div className="h-4 w-full bg-gray-200 rounded" />
                </div>
                <div className="w-40 px-4 py-2 border-r border-[#F0F0F0]">
                  <div className="h-4 w-full bg-gray-200 rounded" />
                </div>
                <div className="w-40 px-4 py-2 border-r border-[#F0F0F0]">
                  <div className="h-4 w-full bg-gray-200 rounded" />
                </div>
                <div className="w-32 px-4 py-2 border-r border-[#F0F0F0]">
                  <div className="h-4 w-full bg-gray-200 rounded" />
                </div>
                <div className="w-32 px-4 py-2">
                  <div className="h-4 w-8 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="min-w-[1200px]">
            <div className="flex bg-neutral-50 border-b border-[#F0F0F0] font-medium text-[#535862] text-sm">
              <div className="w-16 px-4 py-3.5 border-r border-[#F0F0F0]">
                Q.No
              </div>
              <div className="w-96 px-4 py-3.5 border-r border-[#F0F0F0]">
                QuestionText
              </div>
              <div className="w-40 px-4 py-3.5 border-r border-[#F0F0F0]">
                Equation
              </div>
              <div className="w-32 px-4 py-3.5 border-r border-[#F0F0F0]">
                Choice A
              </div>
              <div className="w-32 px-4 py-3.5 border-r border-[#F0F0F0]">
                Choice B
              </div>
              <div className="w-32 px-4 py-3.5 border-r border-[#F0F0F0]">
                Choice C
              </div>
              <div className="w-32 px-4 py-3.5 border-r border-[#F0F0F0]">
                Choice D
              </div>
              <div className="w-32 px-4 py-3.5 border-r border-[#F0F0F0]">
                Correct Answer
              </div>
              <div className="w-40 px-4 py-3.5 border-r border-[#F0F0F0]">
                Topic
              </div>
              <div className="w-40 px-4 py-3.5 border-r border-[#F0F0F0]">
                Sub Topic
              </div>
              <div className="w-32 px-4 py-3.5 border-r border-[#F0F0F0]">
                Difficulty
              </div>
              <div className="w-32 px-4 py-3.5">Action</div>
            </div>
            {data.length > 0 ? (
              data.map((item, index) => (
                <div
                  key={item._id || index}
                  className="flex border-b border-[#F0F0F0] text-[#535862] text-sm bg-white hover:bg-neutral-50 transition-colors"
                >
                  <div className="w-16 px-4 py-2 border-r border-[#F0F0F0] flex items-center">
                    {index + 1}
                  </div>
                  <div className="w-96 px-4 py-2 border-r border-[#F0F0F0] flex items-center">
                    {item.question_text}
                  </div>
                  <div className="w-40 px-4 py-2 border-r border-[#F0F0F0] flex items-center">
                    {item.equation}
                  </div>
                  <div className="w-32 px-4 py-2 border-r border-[#F0F0F0] flex items-center">
                    {item.choice_a}
                  </div>
                  <div className="w-32 px-4 py-2 border-r border-[#F0F0F0] flex items-center">
                    {item.choice_b}
                  </div>
                  <div className="w-32 px-4 py-2 border-r border-[#F0F0F0] flex items-center">
                    {item.choice_c}
                  </div>
                  <div className="w-32 px-4 py-2 border-r border-[#F0F0F0] flex items-center">
                    {item.choice_d}
                  </div>
                  <div className="w-32 px-4 py-2 border-r border-[#F0F0F0] flex items-center">
                    {item.correct_answer}
                  </div>
                  <div className="w-40 px-4 py-2 border-r border-[#F0F0F0] flex items-center">
                    {item.topic_name}
                  </div>
                  <div className="w-40 px-4 py-2 border-r border-[#F0F0F0] flex items-center">
                    {item.subtopic_name}
                  </div>
                  <div className="w-32 px-4 py-2 border-r border-[#F0F0F0] flex items-center">
                    {item.difficulty}
                  </div>
                  <div className="w-32 px-4 py-2 flex items-center">
                    <button
                      className="border bg-neutral-50 p-2 rounded-[10px] border-[#F0F0F0] hover:bg-neutral-100 transition-colors"
                      onClick={() => openModal(item)}
                      title="Edit"
                      aria-label="Edit"
                    >
                      <span className="text-[#535862]">
                        <EditIcon />
                      </span>
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-[#535862] text-sm">
                No Data Available
              </div>
            )}
          </div>
        )}
      </div>
      {/* Edit Modal */}
      {isModalOpen && selectedQuestion && (
        <Modal open={isModalOpen} onClose={closeModal}>
          <div className="bg-white rounded-[20px] border border-[#F0F0F0] w-full max-w-xl p-8">
            <h3 className="text-xl font-bold text-[#030303] mb-6">
              Edit Question
            </h3>
            <form onSubmit={handleUpdate} autoComplete="off">
              {/* Math or Reading/Writing fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                {selectedQuestion?.question_type === 'Math' ? (
                  <>
                    <div>
                      <label className="block text-[#535862] font-medium mb-1">
                        isGridin
                      </label>
                      <select
                        className="w-full px-4 py-2 text-[#535862] bg-[#FAFAFA] border border-[#F0F0F0] rounded-[10px] focus:border-[#DE3C7D]"
                        value={selectedQuestion?.isgridIn || ''}
                        onChange={(e) =>
                          setSelectedQuestion({
                            ...selectedQuestion,
                            isgridIn: e.target.value,
                          })
                        }
                      >
                        <option value="">Select isGridin</option>
                        <option value="FALSE">FALSE</option>
                        <option value="TRUE">TRUE</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[#535862] font-medium mb-1">
                        Equation
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 text-[#535862] bg-[#FAFAFA] border border-[#F0F0F0] rounded-[10px] focus:border-[#DE3C7D]"
                        value={selectedQuestion?.equation || ''}
                        onChange={(e) =>
                          setSelectedQuestion({
                            ...selectedQuestion,
                            equation: e.target.value,
                          })
                        }
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-[#535862] font-medium mb-1">
                        Question Context
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 text-[#535862] bg-[#FAFAFA] border border-[#F0F0F0] rounded-[10px] focus:border-[#DE3C7D]"
                        value={selectedQuestion?.context || ''}
                        onChange={(e) =>
                          setSelectedQuestion({
                            ...selectedQuestion,
                            context: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-[#535862] font-medium mb-1">
                        Question Passage
                      </label>
                      <textarea
                        className="w-full px-4 py-2 text-[#535862] bg-[#FAFAFA] border border-[#F0F0F0] rounded-[10px] focus:border-[#DE3C7D] min-h-[100px]"
                        style={{ height: '200px' }}
                        value={selectedQuestion?.passage || ''}
                        onChange={(e) =>
                          setSelectedQuestion({
                            ...selectedQuestion,
                            passage: e.target.value,
                          })
                        }
                      ></textarea>
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-[#535862] font-medium mb-1">
                    Figure
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 text-[#535862] bg-[#FAFAFA] border border-[#F0F0F0] rounded-[10px] focus:border-[#DE3C7D]"
                    value={selectedQuestion?.figure || ''}
                    onChange={(e) =>
                      setSelectedQuestion({
                        ...selectedQuestion,
                        figure: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-[#535862] font-medium mb-1">
                    Question Text
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 text-[#535862] bg-[#FAFAFA] border border-[#F0F0F0] rounded-[10px] focus:border-[#DE3C7D]"
                    value={selectedQuestion?.question_text || ''}
                    onChange={(e) =>
                      setSelectedQuestion({
                        ...selectedQuestion,
                        question_text: e.target.value,
                      })
                    }
                  />
                </div>
                {selectedQuestion?.isgridIn === 'TRUE' ? (
                  <div>
                    <label className="block text-[#535862] font-medium mb-1">
                      Correct Answer
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 text-[#535862] bg-[#FAFAFA] border border-[#F0F0F0] rounded-[10px] focus:border-[#DE3C7D]"
                      value={selectedQuestion?.correct_answer || ''}
                      onChange={(e) =>
                        setSelectedQuestion({
                          ...selectedQuestion,
                          correct_answer: e.target.value,
                        })
                      }
                    />
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-[#535862] font-medium mb-1">
                        Choice A
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 text-[#535862] bg-[#FAFAFA] border border-[#F0F0F0] rounded-[10px] focus:border-[#DE3C7D]"
                        value={selectedQuestion?.choice_a || ''}
                        onChange={(e) =>
                          setSelectedQuestion({
                            ...selectedQuestion,
                            choice_a: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-[#535862] font-medium mb-1">
                        Choice B
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 text-[#535862] bg-[#FAFAFA] border border-[#F0F0F0] rounded-[10px] focus:border-[#DE3C7D]"
                        value={selectedQuestion?.choice_b || ''}
                        onChange={(e) =>
                          setSelectedQuestion({
                            ...selectedQuestion,
                            choice_b: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-[#535862] font-medium mb-1">
                        Choice C
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 text-[#535862] bg-[#FAFAFA] border border-[#F0F0F0] rounded-[10px] focus:border-[#DE3C7D]"
                        value={selectedQuestion?.choice_c || ''}
                        onChange={(e) =>
                          setSelectedQuestion({
                            ...selectedQuestion,
                            choice_c: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-[#535862] font-medium mb-1">
                        Choice D
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 text-[#535862] bg-[#FAFAFA] border border-[#F0F0F0] rounded-[10px] focus:border-[#DE3C7D]"
                        value={selectedQuestion?.choice_d || ''}
                        onChange={(e) =>
                          setSelectedQuestion({
                            ...selectedQuestion,
                            choice_d: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-[#535862] font-medium mb-1">
                        Correct Answer
                      </label>
                      <select
                        className="w-full px-4 py-2 text-[#535862] bg-[#FAFAFA] border border-[#F0F0F0] rounded-[10px] focus:border-[#DE3C7D]"
                        value={selectedQuestion?.correct_answer || ''}
                        onChange={(e) =>
                          setSelectedQuestion({
                            ...selectedQuestion,
                            correct_answer: e.target.value,
                          })
                        }
                      >
                        <option value="">Select Correct Answer</option>
                        <option value="A">A</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                        <option value="D">D</option>
                      </select>
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-[#535862] font-medium mb-1">
                    Topic
                  </label>
                  <select
                    className="w-full px-4 py-2 text-[#535862] bg-[#FAFAFA] border border-[#F0F0F0] rounded-[10px] focus:border-[#DE3C7D]"
                    value={selectedQuestion?.topic || ''}
                    onChange={(e) =>
                      setSelectedQuestion({
                        ...selectedQuestion,
                        topic: e.target.value,
                      })
                    }
                  >
                    <option value="">Select Topic</option>
                    {Array.isArray(topics) &&
                      topics.map((topic) => (
                        <option key={topic._id} value={String(topic._id)}>
                          {topic.topic_title}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[#535862] font-medium mb-1">
                    Sub Topic
                  </label>
                  <select
                    className="w-full px-4 py-2 text-[#535862] bg-[#FAFAFA] border border-[#F0F0F0] rounded-[10px] focus:border-[#DE3C7D]"
                    value={selectedQuestion?.subtopic || ''}
                    onChange={(e) =>
                      setSelectedQuestion({
                        ...selectedQuestion,
                        subtopic: e.target.value,
                      })
                    }
                  >
                    <option value="">Select Sub Topic</option>
                    {Array.isArray(subtopics) &&
                      subtopics.map((topic) => (
                        <option key={topic._id} value={String(topic._id)}>
                          {topic.topic_title}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[#535862] font-medium mb-1">
                    Difficulty
                  </label>
                  <select
                    className="w-full px-4 py-2 text-[#535862] bg-[#FAFAFA] border border-[#F0F0F0] rounded-[10px] focus:border-[#DE3C7D]"
                    value={selectedQuestion?.difficulty || ''}
                    onChange={(e) =>
                      setSelectedQuestion({
                        ...selectedQuestion,
                        difficulty: e.target.value,
                      })
                    }
                  >
                    <option value="">Select Difficulty</option>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-start gap-3 mt-8">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded-md border border-[#F0F0F0] text-[#535862] bg-white hover:bg-neutral-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-md bg-[#DE3C7D] text-white font-medium hover:bg-[#c8356f] transition-colors"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}
      <ConfirmModal
        open={importModalOpen}
        title="Import Questions"
        message={importModalMessage}
        onCancel={() => setImportModalOpen(false)}
        onConfirm={() => setImportModalOpen(false)}
      />
    </div>
  )
}
export default ExcelImport
